import { computed, reactive, ref } from '@nuxtjs/composition-api';
import { useResult } from '@vue/apollo-composable/dist';
import useCookie, { CookieKeys } from '../useCookie';
import { useI18n } from '../useUtils';
import {
  SearchJobInput,
  SortDirection,
  useCategoryNamesQuery,
  useCityNamesQuery,
  useCorporationTypeNamesQuery,
  useCorporationTypesQuery,
  useGroupIndustriesQuery,
  useIndustryCategoriesQuery,
  useIndustryNamesQuery,
  useJobFeatureNamesQuery,
  useJobFeaturesQuery,
  usePrefectureCitiesQuery,
  JobSort,
} from '~/graphql/graphqlOperations';
import useEnums, { EnumKeys } from '~/compositions/useEnums';

const conditionDefault = {
  corporationId: null,
  keyword: '',
  employmentCodes: [],
  jobNo: '',
  industryCodes: [],
  categoryCodes: [],
  jobFeatures: [],
  corporationTypes: [],
  prefectures: [],
  cities: [],
  showSeniorJob: false,
  schoolIds: [],
  sort: JobSort.Recommend,
  sortDirection: SortDirection.Ascending,
} as SearchJobInput;

interface UseJobSearchOptions {
  temp: boolean;
}

const defaultOptions: UseJobSearchOptions = {
  temp: false,
};

/**
 * 求人票検索のビジネスロジック
 */
export default function useJobsSearch(_options?: UseJobSearchOptions) {
  /**
   * 検索条件の保存、表示ロジック
   * 検索項目はcookie内のJobSettings、JobSettingsTemporaryに保存される。
   * JobSettings：検索ボタンクリックしたときの検索条件保存用。
   * JobSettingsTemporary：検索条件の一時保存用。
   * URLが'/jobs_search'ではない時、JobSettingsTemporaryは削除。(pages/jobs_search/index)
   * JobSettingsTemporaryが必要な理由は、検索項目を選んだが、検索しなかった場合(戻るボタンなどで画面を離れた場合)、募集一覧の検索枠に表示されないという状態を作るため。
   *
   */

  // Object.assign：第一引数に第二引数をコピーするメソッド
  const options = Object.assign(defaultOptions, _options);

  const { getCookie, setCookie } = useCookie();

  // ラベル用
  const { getLabel: getPrefecgureLabel } = useEnums(EnumKeys.Prefecture);

  // 検索条件を取得
  let readKey = CookieKeys.JobSettings;
  if (options.temp) {
    // クッキー(JobSettingsTemporary)に一時保存
    const tempValue = getCookie(CookieKeys.JobSettingsTemporary);
    if (tempValue) {
      // readKeyの値をJobSettingsTemporaryに変更
      readKey = CookieKeys.JobSettingsTemporary;
    }
  }

  // 検索条件を保存 or 一時保存
  // 検索実行時はfalseが渡ってくるのでJobSettingsが適用される
  const saveConditions = (temp: boolean = true) => {
    const whiteKey = temp
      ? CookieKeys.JobSettingsTemporary
      : CookieKeys.JobSettings;
    setCookie(whiteKey, JSON.stringify(conditions));
  };

  const conditions = reactive<SearchJobInput>(
    Object.assign({}, conditionDefault, getCookie(readKey))
  );

  // 検索条件をすべてクリア
  const clearConditions = () => {
    conditions.corporationId = conditionDefault.corporationId;
    conditions.keyword = conditionDefault.keyword;
    conditions.employmentCodes?.splice(0);
    conditions.industryCodes?.splice(0);
    conditions.categoryCodes?.splice(0);
    conditions.jobFeatures?.splice(0);
    conditions.corporationTypes?.splice(0);
    conditions.jobNo = conditionDefault.jobNo;
    conditions.prefectures?.splice(0);
    conditions.cities?.splice(0);
    conditions.showSeniorJob = conditionDefault.showSeniorJob;
    conditions.schoolIds?.splice(0);
    inputedFreeWord.value = conditions.keyword || '';
    selectedSchoolIds.value = conditions.schoolIds || [];
    selectedEmploymentCodes.value = conditions.employmentCodes || [];
    selectedIndustryCodes.value = conditions.industryCodes || [];
    selectedCategoryCodes.value = conditions.categoryCodes || [];
    selectedJobFeatures.value = conditions.jobFeatures || [];
    selectedCorporationTypes.value = conditions.corporationTypes || [];
    selectedPrefectures.value = conditions.prefectures || [];
    selectedCities.value = conditions.cities || [];
    inputedJobNo.value = conditions.jobNo || '';
    checkedShowSeniorJob.value = conditions.showSeniorJob || false;
    // console.log('clearConditions', conditions);
    // クリアして検索条件を保存する
    // 一時的にクリアする場合は引数のfalseを削除
    saveConditions(false);
  };

  /**
   * 都道府県
   */
  // 都道府県ラベル
  const prefecturesLabel = computed(() =>
    (conditions.prefectures || [])
      .map((item) => getPrefecgureLabel(item))
      .join('、')
  );

  // 都道府県クリア
  const clearPrefectures = () => {
    conditions.prefectures?.splice(0);
    conditions.cities?.splice(0);
    saveConditions();
  };

  // 選択済み都道府県
  const selectedPrefectures = ref(conditions.prefectures || []);

  /**
   * 市区町村
   */
  // 都道府県に関連する市区町村情報を取得
  const prefectureCitiesQueryVariables = reactive({
    prefectureCodes: conditions.prefectures || [],
  });
  const prefectureCitiesQueryOptions = reactive({
    enabled: false,
  });
  const { result: prefectureCitiesQueryResult } = usePrefectureCitiesQuery(
    prefectureCitiesQueryVariables,
    prefectureCitiesQueryOptions
  );
  const prefectureCities = useResult(prefectureCitiesQueryResult, []);
  const getPrefectureCities = () => {
    prefectureCitiesQueryVariables.prefectureCodes =
      conditions.prefectures || [];
    prefectureCitiesQueryOptions.enabled = true;
    return prefectureCities;
  };

  // 市区町村名を取得
  const cityNamesQueryVariables = reactive({
    cityCodes: conditions.cities || [],
  });
  const cityNamesQueryOptions = reactive({
    enabled: false,
  });
  const { result: cityNamesQueryResult } = useCityNamesQuery(
    cityNamesQueryVariables,
    cityNamesQueryOptions
  );
  const cityNames = useResult(cityNamesQueryResult, '');
  const citiesLabel = computed(() => {
    if (conditions.cities?.length) {
      cityNamesQueryOptions.enabled = true;
      return cityNames.value;
    } else {
      return '';
    }
  });

  // 市区町村クリア
  const clearCities = () => {
    conditions.cities?.splice(0);
    saveConditions();
  };

  // 選択済み市区町村
  const selectedCities = ref(conditions.cities || []);

  /**
   * 業種
   */
  // グループ分けした業種を取得
  const groupIndustriesQueryVariables = reactive({
    schoolId: 0,
  });
  const groupIndustriesQueryOptions = reactive({
    enabled: false,
  });
  const { result: groupIndustriesQueryResult } = useGroupIndustriesQuery(
    groupIndustriesQueryVariables,
    groupIndustriesQueryOptions
  );
  const groupIndustries = useResult(groupIndustriesQueryResult, []);
  const getGroupIndustries = (schoolId?: number) => {
    groupIndustriesQueryVariables.schoolId = schoolId || 0;
    groupIndustriesQueryOptions.enabled = true;
    return groupIndustries;
  };

  // 業種名を取得
  const industryNamesQueryVariables = reactive({
    industryCodes: conditions.industryCodes || [],
  });
  const industryNamesQueryOptions = reactive({
    enabled: false,
  });
  const { result: industryNamesQueryResult } = useIndustryNamesQuery(
    industryNamesQueryVariables,
    industryNamesQueryOptions
  );
  const industryNames = useResult(industryNamesQueryResult, '');
  const industriesLabel = computed(() => {
    if (conditions.industryCodes?.length) {
      industryNamesQueryOptions.enabled = true;
      return industryNames.value;
    } else {
      return '';
    }
  });

  // 業種クリア
  const clearIndustries = () => {
    conditions.industryCodes?.splice(0);
    saveConditions();
  };

  // 選択済み業種
  const selectedIndustryCodes = ref(conditions.industryCodes || []);

  /**
   * 職種
   */
  // 職種一覧取得
  const categoriesQueryVariables = reactive({
    schoolId: 0,
    industryCodes: conditions.industryCodes || [],
  });
  const categoriesQueryOptions = reactive({
    enabled: false,
  });
  const { result: categoriesQueryResult } = useIndustryCategoriesQuery(
    categoriesQueryVariables,
    categoriesQueryOptions
  );
  const categories = useResult(categoriesQueryResult, []);
  const getCategories = (schoolId?: number) => {
    categoriesQueryVariables.schoolId = schoolId || 0;
    categoriesQueryVariables.industryCodes = conditions.industryCodes || [];
    categoriesQueryOptions.enabled = true;
    return categories;
  };

  // 職種名を取得
  const categoryNamesQueryVariables = reactive({
    categoryCodes: conditions.categoryCodes || [],
  });
  const categoryNamesQueryOptions = reactive({
    enabled: false,
  });
  const { result: categoryNamesQueryResult } = useCategoryNamesQuery(
    categoryNamesQueryVariables,
    categoryNamesQueryOptions
  );
  const categoryNames = useResult(categoryNamesQueryResult, '');
  const categoriesLabel = computed(() => {
    if (conditions.categoryCodes?.length) {
      categoryNamesQueryVariables.categoryCodes =
        conditions.categoryCodes || [];
      categoryNamesQueryOptions.enabled = true;
      return categoryNames.value;
    } else {
      return '';
    }
  });

  // 職種クリア
  const clearCategories = () => {
    conditions.categoryCodes?.splice(0);
    saveConditions();
  };

  // 選択済み職種
  const selectedCategoryCodes = ref(conditions.categoryCodes || []);

  /**
   * 募集の特徴
   */
  // 募集の特徴一覧取得
  const jobFeaturesQueryOptions = reactive({
    enabled: false,
  });
  const { result: jobFeaturesQueryResult } = useJobFeaturesQuery(
    jobFeaturesQueryOptions
  );
  const jobFeatures = useResult(jobFeaturesQueryResult, []);
  const getJobFeatures = () => {
    jobFeaturesQueryOptions.enabled = true;
    return jobFeatures;
  };

  // 募集の特徴名を取得
  const jobFeatureNamesQueryVariables = reactive({
    jobFeatureCodes: conditions.jobFeatures || [],
  });
  const jobFeatureNamesQueryOptions = reactive({
    enabled: false,
  });
  const { result: jobFeatureNamesQueryResult } = useJobFeatureNamesQuery(
    jobFeatureNamesQueryVariables,
    jobFeatureNamesQueryOptions
  );
  const jobFeatureNames = useResult(jobFeatureNamesQueryResult, '');
  const jobFeaturesLabel = computed(() => {
    if (conditions.jobFeatures?.length) {
      jobFeatureNamesQueryVariables.jobFeatureCodes =
        conditions.jobFeatures || [];
      jobFeatureNamesQueryOptions.enabled = true;
      return jobFeatureNames.value;
    } else {
      return '';
    }
  });

  // 募集の特徴クリア
  const clearJobFeatures = () => {
    conditions.jobFeatures?.splice(0);
    saveConditions();
  };

  // 選択済み募集の特徴
  const selectedJobFeatures = ref(conditions.jobFeatures || []);

  /**
   * 企業タイプ
   */
  // 企業タイプ一覧取得
  const corporationTypesQueryVariables = reactive({
    schoolId: 0,
  });
  const corporationTypesQueryOptions = reactive({
    enabled: false,
  });
  const { result: corporationTypesQueryResult } = useCorporationTypesQuery(
    corporationTypesQueryVariables,
    corporationTypesQueryOptions
  );
  const corporationTypes = useResult(corporationTypesQueryResult, []);
  const getCorporationTypes = (schoolId?: number) => {
    corporationTypesQueryVariables.schoolId = schoolId || 0;
    corporationTypesQueryOptions.enabled = true;
    return corporationTypes;
  };

  // 企業タイプ名を取得
  const corporationTypeNamesQueryVariables = reactive({
    corporationTypeCodes: conditions.corporationTypes || [],
  });
  const corporationTypeNamesQueryOptions = reactive({
    enabled: false,
  });
  const { result: corporationTypeNamesQueryResult } =
    useCorporationTypeNamesQuery(
      corporationTypeNamesQueryVariables,
      corporationTypeNamesQueryOptions
    );
  const corporationTypeNames = useResult(corporationTypeNamesQueryResult, '');
  const corporationTypesLabel = computed(() => {
    if (conditions.corporationTypes?.length) {
      corporationTypeNamesQueryVariables.corporationTypeCodes =
        conditions.corporationTypes || [];
      corporationTypeNamesQueryOptions.enabled = true;
      return corporationTypeNames.value;
    } else {
      return '';
    }
  });

  // 企業タイプクリア
  const clearCorporationTypes = () => {
    conditions.corporationTypes?.splice(0);
    saveConditions();
  };
  // 選択済み企業タイプ
  const selectedCorporationTypes = ref(conditions.corporationTypes || []);

  /**
   * グループ校
   */
  // グループ校クリア
  const clearSchoolIds = () => {
    conditions.schoolIds?.splice(0);
    saveConditions();
  };
  // 選択済みグループ校
  const selectedSchoolIds = ref(conditions.schoolIds || []);

  /**
   * フリーワード
   */
  const inputedFreeWord = ref(conditions.keyword || '');

  /**
   * 雇用形態
   */
  const selectedEmploymentCodes = ref(conditions.employmentCodes || []);

  /**
   * 選択された雇用形態を加工してselectedLabelsに代入
   * TODO：本来は他の検索項目の同様にGraphQLで値を取得したい。現状、該当するGraphQlがないため、フロント側で処理をしている
   * 例:["regular", "part_time_job", "contract"]→ 正社員 アルバイト 契約社員
   */
  const i18n = useI18n();
  const selectedEmploymentLabel = ref('');
  const employmentLabel = computed(() => {
    selectedEmploymentLabel.value = selectedEmploymentCodes.value
      .join(' ')
      .replace('regular', `${i18n.t('enums.employment_type.regular')}`)
      .replace('contract', `${i18n.t('enums.employment_type.contract')}`)
      .replace('senior', `${i18n.t('enums.employment_type.senior')}`)
      .replace('outsourcing', `${i18n.t('enums.employment_type.outsourcing')}`)
      .replace(
        'part_time_job',
        `${i18n.t('enums.employment_type.part_time_job')}`
      );
    return selectedEmploymentLabel.value;
  });

  /**
   * 求人票番号
   */
  const inputedJobNo = ref(conditions.jobNo || '');

  /**
   * 上級生の求人票を表示
   */
  const checkedShowSeniorJob = ref(conditions.showSeniorJob || false);

  /**
   * 選択されたラベルを表示
   */
  const selectedLabels = computed(() => {
    const labels = [
      categoriesLabel.value,
      industriesLabel.value,
      inputedFreeWord.value,
      prefecturesLabel.value,
      citiesLabel.value,
      jobFeaturesLabel.value,
      corporationTypesLabel.value,
      employmentLabel.value,
      // selectedSchoolIds.value,
      inputedJobNo.value,
    ];

    // 検索枠に表示するために整える
    const prepareLabels = labels
      .filter((label) => !!label)
      .join(' ')
      .replace(/、/g, ' ');
    return prepareLabels;
  });

  return {
    conditions,
    clearConditions,
    saveConditions,
    // 都道府県
    selectedPrefectures,
    prefecturesLabel,
    clearPrefectures,
    // 市区町村
    getPrefectureCities,
    citiesLabel,
    clearCities,
    selectedCities,
    // 業種
    getGroupIndustries,
    industriesLabel,
    clearIndustries,
    selectedIndustryCodes,
    // 職種
    getCategories,
    categoriesLabel,
    clearCategories,
    selectedCategoryCodes,
    // 募集の特徴
    getJobFeatures,
    jobFeaturesLabel,
    clearJobFeatures,
    selectedJobFeatures,
    // 企業タイプ
    getCorporationTypes,
    corporationTypesLabel,
    clearCorporationTypes,
    selectedCorporationTypes,
    // グループ校
    clearSchoolIds,
    selectedSchoolIds,
    // フリーワード
    inputedFreeWord,
    // 雇用形態
    selectedEmploymentCodes,
    employmentLabel,
    // 求人票番号
    inputedJobNo,
    // 上級生の求人票を表示する
    checkedShowSeniorJob,
    // 選択されたラベルの表示
    selectedLabels,
  };
}
